import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MapPin, ClipboardCheck, Clock, DollarSign,
  AlertTriangle, Download, CheckCircle2, BarChart3, ShieldCheck,
  Sparkles, HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, LogOut, User as UserIcon } from "lucide-react";
import { useAuth } from "@/lib/authContext";
import { useI18n } from "@/lib/i18nContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import LanguageSelector from "@/components/LanguageSelector";
import PublicFooter from "@/components/PublicFooter";
import PageNav from "@/components/PageNav";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { trackEvent } from "@/lib/analytics";
const LandingPreview = lazy(() => import("@/components/landing/LandingPreview"));
const LandingPricing = lazy(() => import("@/components/landing/LandingPricing"));

const featureKeys = [
  { icon: MapPin, titleKey: "features.location.title", descKey: "features.location.desc" },
  { icon: ClipboardCheck, titleKey: "features.permits.title", descKey: "features.permits.desc" },
  { icon: DollarSign, titleKey: "features.budget.title", descKey: "features.budget.desc" },
  { icon: Clock, titleKey: "features.timeline.title", descKey: "features.timeline.desc" },
  { icon: AlertTriangle, titleKey: "features.mistakes.title", descKey: "features.mistakes.desc" },
  { icon: Download, titleKey: "features.export.title", descKey: "features.export.desc" },
  { icon: BarChart3, titleKey: "features.track.title", descKey: "features.track.desc" },
  { icon: ShieldCheck, titleKey: "features.compliance.title", descKey: "features.compliance.desc" },
];

const howItWorksKeys = [
  { step: "01", titleKey: "how.step1.title", descKey: "how.step1.desc" },
  { step: "02", titleKey: "how.step2.title", descKey: "how.step2.desc" },
  { step: "03", titleKey: "how.step3.title", descKey: "how.step3.desc" },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay, ease: [0.21, 0.47, 0.32, 0.98] as const },
});

export default function Landing() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useI18n();

  const go = (path: string) => () => navigate(path);
  const ctaPath = user ? "/onboarding" : "/auth";

  const goToApp = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    try {
      const { data, error } = await supabase
        .from("locations")
        .select("id")
        .eq("user_id", user.id)
        .limit(1);
      if (!error && Array.isArray(data) && data.length > 0) {
        navigate("/dashboard");
      } else {
        navigate("/onboarding");
      }
    } catch {
      navigate("/onboarding");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out");
      navigate("/");
    } catch {
      toast.error("Could not sign out");
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SEO
        title="Oplif — The Hospitality Operating System"
        description="Oplif is the complete toolkit for restaurants, hotels, cafés and hospitality entrepreneurs. From permits to P&L — open smarter and run better. Free to start."
        canonicalPath="/"
      />
      <JsonLd type="Organization" />
      <JsonLd type="WebSite" />
      {/* ─── NAVBAR ─── */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="container flex items-center justify-between py-4">
          <Link to="/" className="font-heading text-xl font-medium tracking-tight text-foreground">
            oplif<span className="text-primary">.</span>
          </Link>
          <div className="hidden sm:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/features" className="hover:text-foreground transition-colors duration-200">Features</Link>
            <a href="#pricing" className="hover:text-foreground transition-colors duration-200">{t("nav.pricing")}</a>
            <Link to="/blog" className="hover:text-foreground transition-colors duration-200">Blog</Link>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Link
              to="/help"
              aria-label="Help Center"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <HelpCircle className="h-5 w-5" />
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-2">
                    <UserIcon className="w-4 h-4" />
                    <span className="hidden sm:inline max-w-[140px] truncate">
                      {user.email ?? "Account"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">
                    {user.email ?? "Signed in"}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={goToApp} className="cursor-pointer">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Go to dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  asChild
                  className="border-[#e76f00] text-[#e76f00] hover:bg-[#e76f00]/10 hover:text-[#e76f00]"
                >
                  <Link to="/demo" onClick={() => trackEvent("nav_cta_click", { cta: "see_live_demo" })}>
                    See Live Demo
                  </Link>
                </Button>
                <Button
                  size="sm"
                  onClick={go("/auth")}
                  className="shadow-sm hover:shadow-md transition-shadow"
                >
                  Log in
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      <main>
      {/* ─── HERO (deep forest green) ─── */}
      <section className="relative bg-brand-dark text-brand-dark-foreground">
        <div className="container pt-24 sm:pt-32 pb-20 text-center relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-to-b from-accent/20 via-accent/5 to-transparent rounded-full blur-3xl pointer-events-none" />

          <div className="relative">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-medium text-white mb-8">
              <Sparkles className="w-3 h-3 text-accent" /> {t("hero.badge")}
            </span>
          </div>
          <h1 className="relative font-heading text-4xl sm:text-5xl md:text-[3.5rem] lg:text-[4rem] font-medium leading-[1.1] tracking-tight text-white max-w-3xl mx-auto">
            {t("hero.stopGuessing")}{" "}<br className="hidden sm:block" /><span className="text-accent">{t("hero.startOpening")}</span>
          </h1>
          <div className="relative mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="gap-2 text-base px-8 h-12 bg-accent text-accent-foreground hover:bg-accent/90 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200" onClick={() => { trackEvent("hero_cta_click", { cta: "get_started_free" }); navigate(ctaPath); }}>
              Get started free <ArrowRight className="w-4 h-4" />
            </Button>
            <Button size="lg" variant="outline" className="text-base px-8 h-12 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white transition-all duration-200" asChild>
              <Link to="/features">See how it works <ArrowRight className="w-4 h-4" /></Link>
            </Button>
          </div>
          <div className="relative mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <Link
              to="/onboarding"
              onClick={() => trackEvent("hero_link_click", { link: "opening_soon" })}
              className="group rounded-xl border border-white/15 bg-white/5 px-5 py-5 hover:bg-white/10 hover:border-white/25 transition-all duration-300"
            >
              <h3 className="text-sm font-medium text-white mb-2">Opening a business</h3>
              <div className="space-y-1">
                <p className="text-xs text-white/60 leading-relaxed">Permits & licenses mapped</p>
                <p className="text-xs text-white/60 leading-relaxed">Startup budget & timeline</p>
                <p className="text-xs text-white/60 leading-relaxed">Location guidance</p>
              </div>
            </Link>
            <Link
              to="/onboarding"
              onClick={() => trackEvent("hero_link_click", { link: "already_open" })}
              className="group rounded-xl border border-white/15 bg-white/5 px-5 py-5 hover:bg-white/10 hover:border-white/25 transition-all duration-300"
            >
              <h3 className="text-sm font-medium text-white mb-2">Already open</h3>
              <div className="space-y-1">
                <p className="text-xs text-white/60 leading-relaxed">P&L & daily revenue tracking</p>
                <p className="text-xs text-white/60 leading-relaxed">Menu engineering</p>
                <p className="text-xs text-white/60 leading-relaxed">Compliance & health score</p>
              </div>
            </Link>
          </div>
          <p className="relative mt-5 text-xs text-white/60 text-center">
            {t("hero.freeToStart")}
          </p>
        </div>

      </section>

      {/* ─── INTEGRATIONS LOGO BAR ─── */}
      <section className="bg-muted/40 border-y border-border">
        <div className="container py-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10">
            <span className="text-[11px] font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Works with the tools you already use
            </span>
            <div className="flex items-center gap-8 sm:gap-12 text-muted-foreground/70">
              {/* Square wordmark */}
              <span aria-label="Square" className="text-[15px] font-bold tracking-tight">Square</span>
              {/* Stripe — wordmark */}
              <svg aria-label="Stripe" role="img" viewBox="0 0 60 25" className="h-5 w-auto fill-current" xmlns="http://www.w3.org/2000/svg">
                <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 9.01c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.58-.24 1.58-1C6.31 13.83 0 14.84 0 10.2 0 7.29 2.16 5.55 5.5 5.55c1.5 0 3 .23 4.5.83v3.88a10.4 10.4 0 0 0-4.5-1.16c-.87 0-1.41.25-1.41.9 0 1.85 6.34.97 6.34 5.65z"/>
              </svg>
              {/* Google Reviews — Google wordmark + Reviews */}
              <div aria-label="Google Reviews" className="flex items-center gap-2">
                <svg role="img" viewBox="0 0 272 92" className="h-5 w-auto" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
                  <path fill="#EA4335" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
                  <path fill="#FBBC05" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/>
                  <path fill="#34A853" d="M225 3v65h-9.5V3h9.5z"/>
                  <path fill="#EA4335" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/>
                  <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/>
                </svg>
                <span className="text-[15px] font-medium tracking-tight">Reviews</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY OPLIF EXISTS ─── */}
      <section className="bg-brand-dark text-brand-dark-foreground py-20 sm:py-28">
        <div className="container text-center">
          <motion.div {...fade()}>
            <span className="text-[11px] font-medium uppercase tracking-[0.15em] text-white/60 mb-6 block">
              Why Oplif Exists
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-medium text-white max-w-3xl mx-auto leading-[1.15] tracking-tight mb-6">
              Most operators overspend opening by $30k–60k. Then leave $2k–5k on the table every month. Oplif fixes both.
            </h2>
            <p className="text-white/80 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
              Permits missed. Timelines underestimated. Costs nobody warned you about. It happens to operators with decades of experience. Oplif makes sure it never happens to you.
            </p>
            <div className="flex justify-center mb-10">
              <Button
                size="lg"
                className="gap-2 text-base px-8 h-12 bg-accent text-accent-foreground hover:bg-accent/90 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
                onClick={() => { trackEvent("why_cta_click", { cta: "get_my_roadmap" }); navigate(ctaPath); }}
              >
                Get my roadmap <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="w-12 h-px bg-white/20 mx-auto mb-6" />
            <p className="text-white/60 text-base max-w-xl mx-auto leading-relaxed">
              Enrico Carloni — Founder & CEO · Hospitality expert , Miami
            </p>
            <p className="text-white/40 text-sm mt-2 max-w-xl mx-auto">
              He navigated 13 permits just to open in Miami. That's why Oplif exists.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── LIVE PREVIEW ─── */}
      <section id="preview" className="container pb-32">
        <motion.div {...fade()}>
          <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
            <LandingPreview onSignUp={go(ctaPath)} />
          </Suspense>
        </motion.div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how" className="container pb-32">
        <motion.div {...fade()} className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-foreground mb-4">{t("how.title")}</h2>
          <p className="text-muted-foreground max-w-md mx-auto">{t("how.subtitle")}</p>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-8 sm:gap-12">
          {howItWorksKeys.map((s, i) => (
            <motion.div key={s.step} {...fade(0.12 * i)} className="text-center group">
              <div className="font-heading text-6xl font-medium text-primary/10 mb-4 group-hover:text-primary/20 transition-colors duration-300">{s.step}</div>
              <h3 className="font-heading font-medium text-foreground text-lg mb-2">{t(s.titleKey)}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{t(s.descKey)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="container pb-32">
        <motion.div {...fade()} className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-foreground mb-4">{t("features.title")}</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">{t("features.subtitle")}</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featureKeys.map((f, i) => (
            <motion.div key={f.titleKey} {...fade(0.06 * i)}
              className="group bg-card rounded-xl p-6 border border-border hover:border-primary/20 hover:shadow-elevated transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/12 transition-colors duration-300">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading font-medium text-foreground text-[15px] mb-2">{t(f.titleKey)}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{t(f.descKey)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section id="pricing" className="container pb-32">
        <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
          <LandingPricing onGetStarted={go(ctaPath)} />
        </Suspense>
      </section>

      {/* ─── FINAL CTA (deep forest green) ─── */}
      <section className="bg-brand-dark text-brand-dark-foreground py-20 sm:py-28">
        <div className="container">
          <motion.div {...fade()} className="relative max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-white mb-4">{t("cta.title")}</h2>
            <p className="text-white/75 mb-8 max-w-md mx-auto">
              {t("cta.subtitle")}
            </p>
            <Button size="lg" className="gap-2 h-12 px-8 bg-accent text-accent-foreground hover:bg-accent/90 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200" onClick={go(ctaPath)}>
              {t("cta.button")} <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="mt-4 text-xs text-white/60">{t("cta.free")}</p>
          </motion.div>
        </div>
      </section>

      </main>
      <PageNav currentPath="/" />
      <PublicFooter />
    </div>
  );
}
